import { DatePipe } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { translate } from '@jsverse/transloco';
import moment from 'moment';

import { AppSharedModule } from 'app/app-shared.module';
import { WidgetDataSource } from 'app/models';
import {
  AnalyticItemFilterSets,
  FilterService,
} from 'app/modules/collection/services/filter.service';
import { Comparison, Widget, WidgetGroup } from 'app/providers/bridge-api/dashboard.service';
import { TimezoneService } from 'app/services/timezone.service';
import { Utils } from 'app/utils/utils';
import { WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { SummarizeLatestArticle } from './journalists/article';
import { EmploymentCenter } from './journalists/employment-center.service';
import { Journalist, Story, StoryContent } from './journalists/journalist';

@Component({
  selector: 'app-newspaper-widglet',
  imports: [AppSharedModule],
  providers: [DatePipe],
  templateUrl: './newspaper-widglet.component.html',
  styleUrls: ['./newspaper-widglet.component.scss'],
})
export class NewspaperWidgletComponent implements OnInit, OnChanges {
  @Input() widget: Widget<WidgetChartOptions>;
  @Input() dataSource: WidgetDataSource;
  @Input() filterSet?: AnalyticItemFilterSets;
  @Input() widgetGroup?: WidgetGroup;
  @Input() comparison?: Comparison;
  @Input() weightField?: string;
  @Input() reportData;

  private employmentCenter = inject(EmploymentCenter);
  private timezoneService = inject(TimezoneService);
  private filterService = inject(FilterService);
  private datePipe = inject(DatePipe);

  hasChanges;
  isGenerating;

  stories: Story[];
  journalist: Journalist;

  ngOnInit(): void {
    this.journalist = this.employmentCenter.createJournalist(this.widget);
    this.stories = this.widget.payload.newspaper.stories;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.reportData?.currentValue) {
      this.hasChanges = this.checkForChanges();
      if (this.hasChanges && !this.widget.payload.newspaper.stories) {
        this.generateNewspaper();
      }
    }
  }

  private checkForChanges() {
    const filterHash = this.hashProperty(this.filterSet);
    const comparisonHash = this.hashProperty(this.comparison);
    const widgetGroupHash = this.hashProperty(this.widgetGroup);
    const widgetSettingsHash = this.journalist.widgetSettingsHash();
    if (
      this.widget.payload.newspaper.generatedAt &&
      //eslint-disable-next-line eqeqeq
      this.widget.payload.newspaper.weightField == this.weightField &&
      this.widget.payload.newspaper.dataSourceId === this.dataSource.id &&
      this.widget.payload.newspaper.dataSourceModifiedAt ===
        this.dataSource.modifiedAt.toISOString() &&
      this.widget.payload.newspaper.timezone === this.timezoneService.timezone &&
      this.widget.payload.newspaper.filterSetHash === filterHash &&
      this.widget.payload.newspaper.comparisonHash === comparisonHash &&
      this.widget.payload.newspaper.widgetGroupHash === widgetGroupHash &&
      this.widget.payload.newspaper.widgetSettingsHash === widgetSettingsHash
    ) {
      return false;
    }
    return true;
  }

  generateNewspaper() {
    this.hasChanges = false;
    this.isGenerating = true;

    const filterHash = this.hashProperty(this.filterSet);
    const comparisonHash = this.hashProperty(this.comparison);
    const widgetGroupHash = this.hashProperty(this.widgetGroup);
    const widgetSettingsHash = this.journalist.widgetSettingsHash();

    delete this.widget.payload.newspaper.generatedAt;
    this.widget.payload.newspaper.weightField = this.weightField;
    this.widget.payload.newspaper.dataSourceId = this.dataSource.id;
    this.widget.payload.newspaper.dataSourceModifiedAt = this.dataSource.modifiedAt.toISOString();
    this.widget.payload.newspaper.timezone = this.timezoneService.timezone;
    this.widget.payload.newspaper.filterSetHash = filterHash;
    this.widget.payload.newspaper.comparisonHash = comparisonHash;
    this.widget.payload.newspaper.widgetGroupHash = widgetGroupHash;
    this.widget.payload.newspaper.widgetSettingsHash = widgetSettingsHash;

    const documentQueryOptions = [
      this.filterService.getWidgetQueryOptions(
        this.widget,
        this.dataSource,
        this.widgetGroup,
        this.comparison?.filters[0]
      ),
    ];

    if (this.comparison?.filters.length > 1) {
      for (let index = 1; index < this.comparison.filters.length; index++) {
        documentQueryOptions.push(
          this.filterService.getWidgetQueryOptions(
            this.widget,
            this.dataSource,
            this.widgetGroup,
            this.comparison.filters[index]
          )
        );
      }
    }

    const articles = [
      new SummarizeLatestArticle(
        this.widget.payload.newspaper.scene,
        this.widget.payload.projectId,
        this.dataSource.collectionName,
        this.dataSource.groupId,
        this.dataSource.analysisId,
        this.timezoneService.timezone,
        documentQueryOptions,
        this.reportData
      ),
    ];
    this.stories =
      articles.flatMap((article) => {
        const story = this.journalist.writeStory(article, () => {
          delete this.widget.payload.newspaper.generatedAt;
          this.hasChanges = true;
        });
        if (story) {
          return [story];
        }
        return [];
      }) ?? [];
    this.widget.payload.newspaper.generatedAt = moment().utc().toISOString();
    this.widget.payload.newspaper.stories = this.stories;
    this.isGenerating = false;
  }

  private hashProperty(prop) {
    if (!prop) {
      return 0;
    }
    return Utils.hashCode(JSON.stringify(prop));
  }

  hasInsight(storyContent: StoryContent) {
    return storyContent.insight !== undefined;
  }

  exportAsCsv(): { fileName?: string; data: any } {
    if (!this.stories?.length) {
      return;
    }

    let csvContent = `${translate('widgets.newspaper-widglet.header')},\n${translate(
      'widgets.newspaper-widglet.subheader',
      {
        date: this.datePipe.transform(this.creationDate, 'yyyy-MM-dd HH:mm:ss'),
      }
    )},\n`;

    for (const story of this.stories) {
      csvContent += `${story.heading},\n,\n`;

      for (const content of story.contents) {
        const subHeaderParts = content.subHeader.replace('<b>', '').replace('</b>', '').split(':');

        csvContent += `${Utils.escapeCsvString(subHeaderParts[0])},${Utils.escapeCsvString(
          subHeaderParts[1]
        )}\n`;

        if (this.hasInsight(content)) {
          csvContent += `,\n${translate(
            'widgets.newspaper-widglet.insight'
          )},${Utils.escapeCsvString(content.insight)}\n,\n`;
        }
      }
    }

    return { data: csvContent };
  }

  get creationDate() {
    return this.widget.payload.newspaper.generatedAt;
  }
}
